import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import { useBaseDatosListState } from "../../components/BaseDatosLayout";
import ElaboradosTable from "../components/ElaboradosTable";
import { getPreparedProducts } from "../api/preparedProductsApi";
import {
  formatPreparedProductSectors,
  PREPARED_PRODUCT_SECTORS,
} from "../utils/preparedProductSector.utils";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 700;

const selectSx = {
  width: { xs: "100%", md: 126 },
  height: 38,
  borderRadius: "33px",
  bgcolor: "neutral.100",
  color: "primary.900",
  fontSize: 13,
  fontWeight: 700,
  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "secondary.500",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    py: 0,
  },
};

function FilterSelect({ value, onValueChange, placeholder, getLabel, children }) {
  return (
    <Select
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      displayEmpty
      size="small"
      IconComponent={value ? () => null : undefined}
      sx={{
        ...selectSx,
        "& .MuiSelect-select": {
          ...selectSx["& .MuiSelect-select"],
          pr: value ? "32px !important" : undefined,
        },
      }}
      renderValue={(selectedValue) =>
        selectedValue ? getLabel?.(selectedValue) || selectedValue : placeholder
      }
      endAdornment={
        value ? (
          <InputAdornment
            position="end"
            sx={{ position: "absolute", right: 8, color: "primary.900" }}
          >
            <IconButton
              size="small"
              aria-label={`Limpiar filtro ${placeholder.toLowerCase()}`}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                onValueChange("");
              }}
              sx={{
                width: 24,
                height: 24,
                opacity: 0.7,
                "&:hover": { opacity: 1 },
              }}
            >
              <ClearIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </InputAdornment>
        ) : null
      }
    >
      {children}
    </Select>
  );
}

const BASE_UNIT_LABELS = {
  g: "Gramos (g)",
  ml: "Mililitros (ml)",
  unit: "Unidad",
  package: "Paquete",
};

function unwrapHttp(res) {
  if (res && typeof res === "object" && "data" in res && !("meta" in res)) {
    return res.data;
  }
  return res;
}

function errorToMessage(err) {
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.data?.message ||
    err?.message ||
    "Error al cargar elaborados."
  );
}

function formatYield(value) {
  if (value === null || value === undefined || value === "") return "-";

  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return String(value);

  return `${(numberValue * 100).toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  })}%`;
}

function formatQuantity(value, unit) {
  if (value === null || value === undefined || value === "") return "-";

  const numberValue = Number(value);
  const quantity = Number.isFinite(numberValue)
    ? numberValue.toLocaleString("es-AR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      })
    : String(value);

  return unit ? `${quantity} ${unit}` : quantity;
}

function normalizePreparedProduct(preparedProduct) {
  if (!preparedProduct) return null;

  const unitLabel =
    BASE_UNIT_LABELS[preparedProduct.resultUnit] || preparedProduct.resultUnit || "-";

  return {
    ...preparedProduct,
    name: preparedProduct.name || "-",
    sectorLabel: formatPreparedProductSectors(preparedProduct.sector),
    resultUnitLabel: unitLabel,
    yieldLabel: formatYield(preparedProduct.yieldFactor),
    generatedQuantityLabel: preparedProduct.recipe
      ? formatQuantity(preparedProduct.recipe.resultQuantity, preparedProduct.resultUnit)
      : "-",
    hasRecipe: Boolean(preparedProduct.recipe?.id),
    isActive: Boolean(preparedProduct.isActive),
  };
}

function CardShell({ children, sx }) {
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "neutral.300",
        borderRadius: "30px",
        boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default function ElaboradosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const latestRequestId = useRef(0);
  const [remembered, remember] = useBaseDatosListState("elaborados", {
    page: 1, sectorFilter: "", statusFilter: "", nameFilterInput: "", nameFilter: "",
  });

  const [preparedProducts, setPreparedProducts] = useState([]);
  const [page, setPage] = useState(remembered.page);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sectorFilter, setSectorFilter] = useState(remembered.sectorFilter);
  const [statusFilter, setStatusFilter] = useState(remembered.statusFilter);
  const [nameFilterInput, setNameFilterInput] = useState(remembered.nameFilterInput);
  const [nameFilter, setNameFilter] = useState(remembered.nameFilter);
  const [successMessage, setSuccessMessage] = useState(location.state?.successMessage || "");

  useEffect(() => {
    if (!location.state?.successMessage) return;
    setSuccessMessage(location.state.successMessage);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    remember({ page, sectorFilter, statusFilter, nameFilterInput, nameFilter });
  }, [nameFilter, nameFilterInput, page, remember, sectorFilter, statusFilter]);

  const fetchPreparedProducts = useCallback(async (signal) => {
    const requestId = ++latestRequestId.current;
    setLoading(true);
    setError("");

    try {
      const res = await getPreparedProducts({
        page,
        limit: PAGE_SIZE,
        name: nameFilter || undefined,
        sector: sectorFilter || undefined,
        isActive: statusFilter || undefined,
        signal,
      });
      const data = unwrapHttp(res);

      if (signal?.aborted || requestId !== latestRequestId.current) return;

      const list = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];

      const normalized = list
        .map(normalizePreparedProduct)
        .filter((item) => item?.id != null);

      setPreparedProducts(normalized);
      setTotalItems(data?.meta?.totalItems ?? normalized.length);
      setTotalPages(data?.meta?.totalPages ?? (normalized.length > 0 ? 1 : 0));
    } catch (err) {
      if (signal?.aborted || requestId !== latestRequestId.current) return;
      setPreparedProducts([]);
      setTotalItems(0);
      setTotalPages(0);
      setError(errorToMessage(err));
    } finally {
      if (!signal?.aborted && requestId === latestRequestId.current) {
        setLoading(false);
      }
    }
  }, [nameFilter, page, sectorFilter, statusFilter]);

  const commitNameFilter = useCallback(
    (value) => {
      const normalized = String(value ?? "").trim();
      if (nameFilter === normalized) return;

      setNameFilter(normalized);
      setPage(1);
    },
    [nameFilter]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchPreparedProducts(controller.signal);

    return () => controller.abort();
  }, [fetchPreparedProducts]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      commitNameFilter(nameFilterInput);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [commitNameFilter, nameFilterInput]);

  const pageSummary = useMemo(() => {
    if (!totalItems) return "Sin elaborados para mostrar";
    return `Mostrando ${preparedProducts.length} de ${totalItems} elaborados`;
  }, [preparedProducts.length, totalItems]);

  function handleClearNameFilter(event) {
    event.stopPropagation();
    setNameFilterInput("");
    setNameFilter("");
    setPage(1);
  }

  function handleNameFilterKeyDown(event) {
    if (event.key !== "Enter") return;
    event.preventDefault();
    event.stopPropagation();
    commitNameFilter(nameFilterInput);
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        overflow: { xs: "auto", md: "hidden" },
      }}
    >
      <StockPageHeader
        title="Elaborados"
        onBack={() => navigate("/stock")}
      />

      <CardShell sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ alignItems: { xs: "stretch", sm: "center" } }}
            >
              <FilterSelect
                value={sectorFilter}
                onValueChange={(value) => {
                  setSectorFilter(value);
                  setPage(1);
                }}
                placeholder="Sector"
                getLabel={(value) => value}
              >
                <MenuItem value="">Todos los sectores</MenuItem>
                {PREPARED_PRODUCT_SECTORS.map((sector) => (
                  <MenuItem key={sector} value={sector}>
                    {sector}
                  </MenuItem>
                ))}
              </FilterSelect>
              <FilterSelect
                value={statusFilter}
                onValueChange={(value) => {
                  setStatusFilter(value);
                  setPage(1);
                }}
                placeholder="Estado"
                getLabel={(value) => (value === "true" ? "Activo" : "Inactivo")}
              >
                <MenuItem value="">Todos los estados</MenuItem>
                <MenuItem value="true">Activo</MenuItem>
                <MenuItem value="false">Inactivo</MenuItem>
              </FilterSelect>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ alignItems: { xs: "stretch", sm: "center" } }}
            >
              <TextField
                value={nameFilterInput}
                onChange={(event) => setNameFilterInput(event.target.value)}
                onBlur={(event) => commitNameFilter(event.target.value)}
                onKeyDown={handleNameFilterKeyDown}
                placeholder="Buscar"
                size="small"
                slotProps={{ htmlInput: { maxLength: 200 } }}
                sx={{
                  width: { xs: "100%", md: 200 },
                  "& .MuiOutlinedInput-root": {
                    height: 38,
                    borderRadius: "33px",
                    bgcolor: "neutral.50",
                    color: "primary.900",
                    fontSize: 13,
                    fontWeight: 600,
                    boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "neutral.700",
                    opacity: 0.9,
                    fontWeight: 600,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "primary.900", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: nameFilterInput ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Limpiar búsqueda"
                        onClick={handleClearNameFilter}
                        sx={{
                          color: "primary.900",
                          width: 24,
                          height: 24,
                          opacity: 0.7,
                          "&:hover": { opacity: 1 },
                        }}
                      >
                        <ClearIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              <Button
                component={RouterLink}
                to="/stock/base-de-datos/elaborados/crear"
                sx={{
                  minWidth: { xs: "100%", sm: 142 },
                  height: 38,
                  px: 2.4,
                  borderRadius: "33px",
                  bgcolor: "primary.500",
                  color: "logoblanco",
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 800,
                  boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
                  "&:hover": { bgcolor: "primary.600" },
                }}
              >
                Agregar elaborados
              </Button>
            </Stack>
          </Stack>

          {!!error && (
            <Box
              sx={{
                px: 2,
                py: 1.2,
                borderRadius: "18px",
                bgcolor: "secondary.700",
                boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "logoblanco" }}>
                No se pudo completar la operación
              </Typography>
              <Typography
                sx={{ fontSize: 12, fontWeight: 600, color: "logoblanco", opacity: 0.95 }}
              >
                {error}
              </Typography>
            </Box>
          )}

          <ElaboradosTable
            rows={preparedProducts}
            loading={loading}
            emptyMessage={
              nameFilter || sectorFilter || statusFilter
                ? "No se encontraron elaborados con los filtros seleccionados."
                : "Todavía no hay elaborados cargados."
            }
            onViewRecipe={(preparedProduct) =>
              navigate(`/stock/base-de-datos/elaborados/${preparedProduct.id}/receta`)
            }
            onEdit={(preparedProduct) =>
              navigate(`/stock/base-de-datos/elaborados/${preparedProduct.id}/editar`)
            }
          />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            sx={{
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              pt: 0.5,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900" }}>
              {pageSummary}
            </Typography>

            <Pagination
              page={page}
              count={Math.max(totalPages, 1)}
              onChange={(_, value) => setPage(value)}
              shape="rounded"
              color="primary"
              disabled={loading || totalPages <= 1}
            />
          </Stack>
        </Stack>
      </CardShell>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3500}
        onClose={() => setSuccessMessage("")}
        message={successMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Stack>
  );
}
